import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TableContainer,
  CssBaseline,
  Container,
  Card,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search as SearchIcon, PersonRemove } from '@mui/icons-material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import ListPagination, { pickTotal } from "../../components/ListPagination/ListPagination";

const LIMIT = 10;

const ExerciseAssignUser = () => {
  const { colors: appColors } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [exerciseList, setExerciseList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LIMIT);
  const [exerciseId, setExerciseId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [unassigning, setUnassigning] = useState(null);
  const [unassignConfirm, setUnassignConfirm] = useState(null);
  const [exerciseModal, setExerciseModal] = useState(null);
  const [exercisesFullList, setExercisesFullList] = useState([]);

  const getToken = () => JSON.parse(localStorage.getItem('userdata') || '{}')?.token;

  const fetchExerciseDropdown = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${window.constants.api_url}/exercise-dropdown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      const list = Array.isArray(data.data) ? data.data : [];
      setExerciseList(list);
      if (!exerciseId && list.length > 0) setExerciseId(list[0].id);
    } catch (e) {
      console.error('Error fetching exercise dropdown:', e);
    }
  }, []);

  // One page of exercise assignments for the current exercise / search filter.
  const fetchAssignments = useCallback(
    async (pageZero = 0, size = rowsPerPage) => {
      const token = getToken();
      if (!token) return;
      setLoading(true);
      try {
        const body = {
          limit: size,
          index: pageZero * size,
          exercise_id: exerciseId || undefined,
          search_text: searchText || undefined,
        };
        const res = await fetch(`${window.constants.api_url}/exercise-assignments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];
        if (list.length === 0 && pageZero > 0) {
          // The last row of the last page was just removed: step back one page.
          await fetchAssignments(pageZero - 1, size);
          return;
        }
        setAssignments(list);
        setPage(pageZero);
        const total = pickTotal(data);
        setTotalCount(total != null ? total : pageZero * size + list.length);
      } catch (e) {
        console.error('Error fetching exercise assignments:', e);
        setAssignments([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [rowsPerPage, exerciseId, searchText]
  );

  const fetchExercisesFull = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${window.constants.api_url}/exercises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ limit: 500, index: 0 }),
      });
      const data = await res.json();
      setExercisesFullList(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      console.error('Error fetching exercises:', e);
    }
  }, []);

  useEffect(() => {
    fetchExerciseDropdown();
    fetchExercisesFull();
  }, [fetchExerciseDropdown, fetchExercisesFull]);

  useEffect(() => {
    fetchAssignments(0);
  }, [exerciseId, searchText]);

  const handleSearch = () => setSearchText(searchInput.trim());

  const handleExerciseClick = (row) => {
    const full = exercisesFullList.find((e) => e.id === row.exercise_id || e.id === row.exercise?.id);
    const name = row.exercise?.exercise_name ?? row.exercise_name ?? (typeof row.exercise === 'string' ? row.exercise : null) ?? '-';
    setExerciseModal(full || (typeof row.exercise === 'object' && row.exercise != null ? row.exercise : null) || { exercise_name: name });
  };

  const handleUnassign = async () => {
    if (!unassignConfirm) return;
    const { exercise_id, member_id } = unassignConfirm;
    const token = getToken();
    if (!token) return;
    setUnassigning(unassignConfirm);
    try {
      const res = await fetch(`${window.constants.api_url}/exercise-unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ exercise_id, member_id }),
      });
      const data = await res.json();
      if (data.code === 200) {
        fetchAssignments(page);
        setUnassignConfirm(null);
      } else {
        console.error('Unassign failed:', data?.message);
      }
    } catch (e) {
      console.error('Error unassigning:', e);
    } finally {
      setUnassigning(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
        <Container maxWidth="xl">
          {/* Single compact nav bar: tabs + filters */}
          <Card sx={{ mb: 2, borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              spacing={1.5}
              sx={{ px: 1.5, py: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate('/exercise-list')}
                  sx={{ minWidth: 0, px: 0.5, color: 'text.secondary', textTransform: 'none', '&:hover': { color: appColors.evergreen } }}
                >
                  Exercise List
                </Button>
                <Button size="small" variant="text" sx={{ minWidth: 0, px: 0.5, color: appColors.evergreen, textTransform: 'none', fontWeight: 600 }}>
                  Assign User
                </Button>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 140 } }}>
                  <InputLabel>Exercise</InputLabel>
                  <Select value={exerciseId} onChange={(e) => setExerciseId(e.target.value)} label="Exercise">
                    <MenuItem value="">All</MenuItem>
                    {exerciseList.map((ex) => (
                      <MenuItem key={ex.id} value={ex.id}>{ex.exercise_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search member..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    sx: { backgroundColor: 'action.hover', '& fieldset': { borderColor: 'transparent' }, borderRadius: 1 },
                  }}
                  sx={{ width: { xs: 200, sm: 280 } }}
                />
                <Button size="small" variant="outlined" onClick={handleSearch} sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none', minWidth: 0 }}>
                  Search
                </Button>
              </Stack>
            </Stack>
          </Card>

          {/* Table */}
          <Card sx={{ borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: appColors.evergreen }} /></Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: appColors.evergreen }}>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Member</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Exercise</TableCell>
                        {!isMobile && (
                          <>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Membership No.</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Assigned At</TableCell>
                          </>
                        )}
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No exercise assignments found.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        assignments.map((row, i) => (
                          <TableRow key={row.id != null ? row.id : i} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8fafc' }, '&:hover': { bgcolor: '#f1f5f9' } }}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{row.member?.name ?? `Member #${row.member_id ?? '-'}`}</TableCell>
                            <TableCell
                              onClick={() => handleExerciseClick(row)}
                              sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: appColors.evergreen,
                                  fontWeight: 500,
                                  textDecoration: 'underline',
                                  '&:hover': { color: appColors.pine },
                                }}
                              >
                                {row.exercise?.exercise_name ?? row.exercise_name ?? (typeof row.exercise === 'string' ? row.exercise : null) ?? '-'}
                              </Typography>
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell>{row.member?.membership_number ?? '-'}</TableCell>
                                <TableCell>{row.member?.phone ?? '-'}</TableCell>
                                <TableCell>
                                  {row.assigned_at
                                    ? new Date(row.assigned_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                                    : '-'}
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => setUnassignConfirm({ exercise_id: row.exercise_id, member_id: row.member_id })}
                                disabled={unassigning !== null}
                                sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.08)' } }}
                                title="Unassign"
                              >
                                <PersonRemove fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <ListPagination
                  count={totalCount}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(p) => fetchAssignments(p)}
                  onRowsPerPageChange={(size) => { setRowsPerPage(size); fetchAssignments(0, size); }}
                />
              </>
            )}
          </Card>

          {/* Exercise details modal */}
          <Dialog open={Boolean(exerciseModal)} onClose={() => setExerciseModal(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ bgcolor: appColors.evergreen, color: 'white', py: 1.5 }}>
              {exerciseModal?.exercise_name ?? 'Exercise'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {exerciseModal && (
                <Stack spacing={1.5}>
                  {exerciseModal.description != null && exerciseModal.description !== '' && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Description</Typography>
                      <Typography variant="body2">{exerciseModal.description}</Typography>
                    </Box>
                  )}
                  {exerciseModal.instructions != null && exerciseModal.instructions !== '' && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Instructions</Typography>
                      <Typography variant="body2">{exerciseModal.instructions}</Typography>
                    </Box>
                  )}
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Sets</Typography>
                      <Typography variant="body2">{exerciseModal.sets ?? '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Reps</Typography>
                      <Typography variant="body2">{exerciseModal.reps ?? '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Duration</Typography>
                      <Typography variant="body2">{exerciseModal.duration ?? '-'}</Typography>
                    </Box>
                  </Stack>
                  {!exerciseModal.description && !exerciseModal.instructions && (
                    <Typography variant="body2" color="text.secondary">No description or instructions.</Typography>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setExerciseModal(null)} variant="outlined" sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Unassign confirm dialog */}
          <Dialog open={Boolean(unassignConfirm)} onClose={() => setUnassignConfirm(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ bgcolor: appColors.evergreen, color: 'white', py: 2 }}>Unassign Member</DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography>Remove this member from the exercise assignment?</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setUnassignConfirm(null)} variant="outlined" disabled={unassigning} sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>Cancel</Button>
              <Button onClick={handleUnassign} variant="contained" disabled={unassigning} sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>
                {unassigning ? <CircularProgress size={24} /> : 'Unassign'}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default ExerciseAssignUser;
